import React from 'react';
import styled, { css } from 'styled-components';
import {
  SYS_BREAKPOINT_MD,
  SYS_BREAKPOINT_2_XL,
  SYS_BREAKPOINT_XL,
  SYS_BREAKPOINT_LG,
  SYS_BREAKPOINT_XS,
} from '@oneaudi/unified-web-common';
import { UeContainer, UeElement, UeReference } from '@oneaudi/falcon-tools';
import { parseAssetValue } from '../../utils/parseAssetValue';
import type { AssetTypeImage } from '../../../types';
import { Mode } from '../../../types';
import { useWindowSize } from '../../hooks';

const preserveVideoWidthStyles = css`
  min-height: auto;
  height: auto;
`;

const headerHeightOffset = '72px';

export const ResponsiveMediaItemBase = styled.div<{
  mode: Mode;
  preserveVideoWidth?: boolean;
}>`
  object-fit: ${(preserveVideoWidth) => (preserveVideoWidth ? 'contain' : 'cover')};
  height: ${({ mode }) => (mode === Mode.LARGE ? `calc(100vh - ${headerHeightOffset})` : '100%')};
  @media (min-width: ${SYS_BREAKPOINT_XS}) {
    ${({ preserveVideoWidth, mode }) =>
      preserveVideoWidth
        ? preserveVideoWidthStyles
        : css`
            min-height: ${mode === Mode.LARGE ? `calc(640px - ${headerHeightOffset})` : 'auto'};
            height: ${mode === Mode.SMALL ? '640px' : `calc(100vh - ${headerHeightOffset})`};
          `}
  }
  @media (min-width: ${SYS_BREAKPOINT_MD}) {
    ${({ preserveVideoWidth, mode }) =>
      preserveVideoWidth
        ? preserveVideoWidthStyles
        : css`
            min-height: ${mode === Mode.LARGE ? `calc(800px - ${headerHeightOffset})` : 'auto'};
            height: ${mode === Mode.SMALL ? '800px' : `calc(100vh - ${headerHeightOffset})`};
          `}
  }
  @media (min-width: ${SYS_BREAKPOINT_LG}) {
    ${({ preserveVideoWidth, mode }) =>
      preserveVideoWidth
        ? preserveVideoWidthStyles
        : css`
            min-height: ${mode === Mode.LARGE ? `calc(640px - ${headerHeightOffset})` : 'auto'};
            height: ${mode === Mode.SMALL ? '640px' : `calc(100vh - ${headerHeightOffset})`};
          `}
  }
  @media (min-width: ${SYS_BREAKPOINT_2_XL}) {
    ${({ preserveVideoWidth, mode }) =>
      preserveVideoWidth
        ? preserveVideoWidthStyles
        : css`
            min-height: ${mode === Mode.LARGE ? `calc(800px - ${headerHeightOffset})` : 'auto'};
            height: ${mode === Mode.SMALL ? '800px' : `calc(100vh - ${headerHeightOffset})`};
          `}
  }
`;

const MediaItem = styled(ResponsiveMediaItemBase)`
  width: auto;
  position: relative;
`;

const Image = styled.img`
  height: 100%;
  object-fit: cover;
  position: relative;
  width: 100%;
`;

interface StageImageProps {
  asset: AssetTypeImage;
  mode: Mode;
}

const removePxFromValue = (value: string) => +value.replace('px', '');

const StageImage = ({ asset, mode }: StageImageProps) => {
  const { landscapeAltText, portraitAltText, portrait, landscape } = asset;
  const { width } = useWindowSize();
  const imagePortrait = encodeURI(parseAssetValue(portrait?.assetValue ?? '').src);
  const imageLandscape = encodeURI(parseAssetValue(landscape?.assetValue ?? '').src);
  const md = removePxFromValue(SYS_BREAKPOINT_MD);
  const lg = removePxFromValue(SYS_BREAKPOINT_LG);
  const xl = removePxFromValue(SYS_BREAKPOINT_XL);
  const twoXl = removePxFromValue(SYS_BREAKPOINT_2_XL);

  const isMobile = width < Number(md);

  return (
    <UeContainer propertyPath="asset" label="images">
      <MediaItem mode={mode} data-testid="fa-editorial-stage-image" className="mediaItem">
        <UeReference propertyPath="asset" behavior="component">
          {(ueProps) => (
            <picture {...ueProps}>
              {/* < 768 */}
              <UeElement type="media" property="assetPortrait" label="imagePortrait">
                {(ueElemProps) => (
                  <source
                    media={`(max-width: ${md}px)`}
                    srcSet={`${imagePortrait}?width=${md}`}
                    {...ueElemProps}
                  />
                )}
              </UeElement>
              {/* < 1024 */}
              <UeElement type="media" property="assetLandscape" label="imageLandscape">
                {(ueElemProps) => (
                  <source
                    media={`(max-width: ${lg}px)`}
                    srcSet={`${imageLandscape}?width=${lg}`}
                    {...ueElemProps}
                  />
                )}
              </UeElement>
              {/* < 1440 */}
              <UeElement type="media" property="assetLandscape" label="imageLandscape">
                {(ueElemProps) => (
                  <source
                    media={`(max-width: ${xl}px)`}
                    srcSet={`${imageLandscape}?width=${xl}`}
                    {...ueElemProps}
                  />
                )}
              </UeElement>
              {/* >= 1440 */}
              <UeElement type="media" property="assetLandscape" label="imageLandscape">
                {(ueElemProps) => (
                  <source
                    media={`(min-width: ${xl}px)`}
                    srcSet={`${imageLandscape}?width=${twoXl}`}
                    {...ueElemProps}
                  />
                )}
              </UeElement>
              {/* fallback */}
              {/* TODO: remove this portion ? */}
              <UeElement
                type="media"
                property={isMobile ? 'assetPortrait' : 'assetLandscape'}
                label={isMobile ? 'imagePortrait' : 'imageLandscape'}
              >
                {(ueElemProps) => (
                  <Image
                    alt={isMobile ? portraitAltText : landscapeAltText}
                    data-object-fit
                    src={imagePortrait}
                    loading="lazy"
                    {...ueElemProps}
                  />
                )}
              </UeElement>
            </picture>
          )}
        </UeReference>
      </MediaItem>
    </UeContainer>
  );
};

export default StageImage;
